import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { activitiesApi } from '../lib/api';
import { getCategoryInfo, formatDateShort, formatPrice, getNextDate } from '../lib/utils';
import { Heart, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import analytics from '../lib/analytics';

const ActivityCard = ({ activity, isFavorited = false, onFavoriteToggle, compact = false }) => {
  const { t, language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const [currentImage, setCurrentImage] = useState(0);
  const [liked, setLiked] = useState(isFavorited);
  const isCreator = user?.role === 'creator';
  
  const categoryInfo = getCategoryInfo(activity.category);
  const nextDate = getNextDate(activity.dates);
  const spotsLeft = activity.capacity - activity.registered_count;
  const isFull = spotsLeft <= 0;
  const price = formatPrice(activity.price, language === 'es' ? 'es-MX' : 'en-US');

  const images = [
    activity.cover_image,
    ...(activity.gallery || [])
  ].filter(Boolean);

  if (images.length === 0) {
    images.push('https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=800&q=80');
  }

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImage(prev => prev > 0 ? prev - 1 : images.length - 1);
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImage(prev => prev < images.length - 1 ? prev + 1 : 0);
  };

  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar favoritos');
      return;
    }
    try {
      const response = await activitiesApi.toggleFavorite(activity.activity_id);
      setLiked(response.data.favorited);
      analytics.toggleFavorite(activity.activity_id, response.data.favorited);
      if (onFavoriteToggle) onFavoriteToggle(activity.activity_id, response.data.favorited);
    } catch (error) { console.debug('toggleFavorite:', error.message); }
  };

  return (
    <Link 
      to={`/activity/${activity.activity_id}`}
      className="block group"
      data-testid={`activity-card-${activity.activity_id}`}
    >
      {/* Image */}
      <div className="relative rounded-xl overflow-hidden mb-1.5">
        {/* Mobile: single image only (no carousel to avoid scroll conflict) */}
        <div className={`md:hidden ${compact ? 'aspect-square' : 'aspect-[16/10]'}`}>
          <img 
            src={images[0]} 
            alt={activity.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Desktop: single image with hover arrows */}
        <div className={`hidden md:block ${compact ? 'aspect-square' : 'aspect-[16/10]'}`}>
          <img 
            src={images[currentImage]} 
            alt={activity.title}
            className="w-full h-full object-cover transition-transform duration-300"
            loading="lazy"
          />
          {images.length > 1 && (
            <>
              <button 
                onClick={handlePrevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105"
                data-testid="slideshow-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button 
                onClick={handleNextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105"
                data-testid="slideshow-next"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>

        {/* Dots - desktop only */}
        {!compact && images.length > 1 && (
          <div className="hidden md:flex slideshow-dots">
            {images.slice(0, 5).map((_, idx) => (
              <div
                key={`card-dot-${idx}`}
                className={`slideshow-dot ${idx === currentImage ? 'active' : ''}`}
              />
            ))}
          </div>
        )}

        {/* Like button - hidden for creators */}
        {!isCreator && (
          <button 
            onClick={handleLike}
            className="absolute top-2 right-2 heart-animation"
            data-testid={`like-button-${activity.activity_id}`}
          >
            <Heart 
              className={`w-5 h-5 drop-shadow-md transition-colors ${
                liked ? 'fill-red-500 text-red-500' : 'fill-black/30 text-white'
              }`} 
            />
          </button>
        )}

        {isFull && (
          <div className="absolute top-2 left-2 bg-white px-1.5 py-0.5 rounded text-[10px] font-medium text-amber-600">
            Lista de espera
          </div>
        )}
      </div>

      {/* Content */}
      <div className="leading-snug">
        <div className="flex items-start justify-between gap-2 mb-0.5">
          <h3 className="font-medium text-[13px] text-gray-700 line-clamp-2">
            {activity.title}
          </h3>
          {activity.creator?.avg_rating > 0 && (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Star className="w-3 h-3 fill-current" />
              <span className="text-xs">{activity.creator.avg_rating}</span>
            </div>
          )}
        </div>

        <p className="text-[11px] text-gray-400 leading-tight">
          {t(`category.${activity.category?.toLowerCase()}`)} · {activity.city}
        </p>

        {nextDate && (
          <p className="text-[11px] text-gray-400 leading-tight">
            {formatDateShort(nextDate.date, language === 'es' ? 'es-MX' : 'en-US')}
          </p>
        )}

        <p className="mt-0.5">
          {price ? (
            <span className="font-medium text-[13px]">{price}</span>
          ) : (
            <span className="font-medium text-[13px] text-primary">{t('activity.free')}</span>
          )}
          {!isFull && (
            <span className="text-[11px] text-gray-400 ml-1">
              · {spotsLeft} {spotsLeft === 1 ? t('activity.spot') : t('activity.spotsPlural')}
            </span>
          )}
        </p>
      </div>
    </Link>
  );
};

export default ActivityCard;
